<template>
    <div class="docs">
        <page-hero title="Docs" subtitle="Everything you need to get started with Gamebot" />

        <div class="docs-panel">
            <section class="docs-intro">
                <div class="docs-section" :id="introSection.id">
                    <h2 class="section-title">{{ introSection.label }}</h2>
                    <p class="section-subtitle" v-if="introSection.subtitle">{{ introSection.subtitle }}</p>
                    <component :is="introSection.component" />
                </div>
            </section>

            <section class="docs-body">
                <aside class="docs-sidebar">
                    <b-menu class="docs-menu">
                        <b-menu-list label="On this page">
                            <b-menu-item
                                v-for="section in sections"
                                :key="section.id"
                                :label="section.label"
                                :active="activeSection === section.id"
                                @click="scrollTo(section.id)" />
                        </b-menu-list>
                    </b-menu>
                </aside>

                <main class="docs-content">
                    <div class="docs-section" v-for="section in sections" :key="section.id" :id="section.id">
                        <h2 class="section-title">{{ section.label }}</h2>
                        <p class="section-subtitle" v-if="section.subtitle">{{ section.subtitle }}</p>
                        <component :is="section.component" />
                    </div>
                </main>
            </section>
        </div>
    </div>
</template>

<style lang="scss" scoped>
// Needs to sit above the animated hero gradient (App.vue's PageGradient is
// position:absolute; z-index:0, which otherwise paints on top of any plain
// static content that follows it in the DOM) and have an opaque background
// so the gradient can't show through, giving the hero a clean edge to end
// at. On desktop this reads as a floating card (rounded, shadowed); on
// tablet/mobile it's dropped in favor of a flat, edge-to-edge white panel.
.docs-panel {
    position: relative;
    z-index: 2;
    background-color: $white;

    @include desktop {
        max-width: 1200px;
        margin: 0 auto;
        border-radius: 16px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    }
}

.docs-intro {
    padding: 20px 40px 0;
    text-align: left;

    @include mobile {
        padding: 20px 20px 0;
    }
}

.docs-body {
    display: flex;
    padding: 0 40px 80px;
    gap: 40px;
    text-align: left;

    @include mobile {
        flex-direction: column;
        padding: 0 20px 60px;
    }
}

.docs-sidebar {
    flex: 0 0 200px;
    position: sticky;
    top: 90px;
    align-self: flex-start;

    @include mobile {
        position: static;
        width: 100%;
    }
}

.docs-content {
    flex-grow: 1;
    min-width: 0;
}

.docs-section {
    scroll-margin-top: 90px;
    padding: 40px 0;
    border-bottom: 1px solid $gray-light;

    &:last-child {
        border-bottom: none;
    }

    .section-title {
        font-size: 1.75rem;
        font-weight: bold;
        margin-bottom: 8px;
    }

    .section-subtitle {
        color: $gray;
        margin-bottom: 28px;
    }
}
</style>

<script>
import PageHero from '../components/Page/PageHero.vue'
import GettingStarted from '../components/Docs/GettingStarted.vue'
import GameCards from '../components/Docs/GameCards.vue'
import EconomyRewards from '../components/Docs/EconomyRewards.vue'
import CommandReference from '../components/Docs/CommandReference.vue'
import Faq from '../components/Docs/Faq.vue'

export default {
    name: 'Docs',
    components: {
        PageHero,
        GettingStarted,
        GameCards,
        EconomyRewards,
        CommandReference,
        Faq,
    },
    data() {
        return {
            activeSection: 'playing-a-game',
            observer: null,
            introSection: { id: 'getting-started', label: 'Getting Started', subtitle: 'From invite to your first win, in five steps.', component: 'GettingStarted' },
            sections: [
                { id: 'playing-a-game', label: 'Playing a Game', subtitle: 'Every game Gamebot supports, and how to jump in.', component: 'GameCards' },
                { id: 'economy-rewards', label: 'Economy & Rewards', subtitle: 'Credits, gold, XP, achievements, and daily quests.', component: 'EconomyRewards' },
                { id: 'command-reference', label: 'Command Reference', subtitle: 'The full, live list of Gamebot commands.', component: 'CommandReference' },
                { id: 'faq', label: 'FAQ', subtitle: null, component: 'Faq' },
            ],
        }
    },
    methods: {
        scrollTo(id) {
            const el = document.getElementById(id)
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        },
    },
    mounted() {
        this.observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    this.activeSection = entry.target.id
                }
            }
        }, { rootMargin: '-100px 0px -70% 0px' })

        for (const section of this.sections) {
            const el = document.getElementById(section.id)
            if (el) this.observer.observe(el)
        }
    },
    beforeDestroy() {
        if (this.observer) this.observer.disconnect()
    },
}
</script>
